import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import API from '../api';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', client: '',
    status: 'pending', deadline: '', progress: 0
  });

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data } = await API.get('/projects');
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects');
    }
  };

  const fetchClients = async () => {
    try {
      const { data } = await API.get('/clients');
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await API.post('/projects', form);
      setShowModal(false);
      setForm({
        title: '', description: '', client: '',
        status: 'pending', deadline: '', progress: 0
      });
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create project');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this project?')) {
      try {
        await API.delete(`/projects/${id}`);
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project');
      }
    }
  };

  const handleProgressUpdate = async (id, progress) => {
    try {
      await API.put(`/projects/${id}`, { progress });
      fetchProjects();
    } catch (error) {
      console.error('Error updating progress');
    }
  };

  const statusColor = {
    'pending': '#f59e0b',
    'in-progress': '#3b82f6',
    'review': '#8b5cf6',
    'completed': '#10b981'
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.heading}>Projects</h1>
          <p style={styles.subheading}>Track and manage all projects</p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowModal(true)}>
          ➕ New Project
        </button>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div style={styles.empty}>
          <p>No projects yet. Create your first project!</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {projects.map((project) => (
            <div key={project._id} style={styles.card}>
              {/* Card Header */}
              <div style={styles.cardHeader}>
                <h3 style={styles.projectTitle}>{project.title}</h3>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: statusColor[project.status] + '20',
                  color: statusColor[project.status]
                }}>
                  {project.status}
                </span>
              </div>

              {/* Description */}
              <p style={styles.description}>
                {project.description || 'No description'}
              </p>

              {/* Client */}
              <p style={styles.clientName}>
                👤 {project.client?.name || 'Unknown Client'}
              </p>

              {/* Deadline */}
              {project.deadline && (
                <p style={styles.deadline}>
                  📅 {new Date(project.deadline).toLocaleDateString()}
                </p>
              )}

              {/* Progress Bar */}
              <div style={styles.progressSection}>
                <div style={styles.progressLabel}>
                  <span>Progress</span>
                  <span>{project.progress}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{
                    ...styles.progressFill,
                    width: `${project.progress}%`,
                    backgroundColor: statusColor[project.status]
                  }} />
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={project.progress}
                  onChange={(e) =>
                    handleProgressUpdate(project._id, parseInt(e.target.value))
                  }
                  style={styles.slider}
                />
              </div>

              {/* Delete */}
              <button
                style={styles.deleteBtn}
                onClick={() => handleDelete(project._id)}
              >
                🗑️ Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Create New Project</h2>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Project Title *</label>
              <input
                style={styles.input}
                placeholder="Website Redesign"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                style={{ ...styles.input, height: '80px', resize: 'vertical' }}
                placeholder="Project details..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Assign to Client *</label>
              <select
                style={styles.input}
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
              >
                <option value="">Select a client</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Status</label>
              <select
                style={styles.input}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="review">Review</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Deadline</label>
              <input
                style={styles.input}
                type="date"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              />
            </div>

            <div style={styles.modalButtons}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                style={styles.submitBtn}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px'
  },
  heading: {
    margin: '0',
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e'
  },
  subheading: {
    margin: '4px 0 0 0',
    color: '#718096',
    fontSize: '15px'
  },
  addBtn: {
    padding: '12px 24px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  empty: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '60px',
    textAlign: 'center',
    color: '#718096',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  projectTitle: {
    margin: '0',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a2e'
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  description: {
    margin: '0 0 12px 0',
    color: '#718096',
    fontSize: '14px'
  },
  clientName: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#4a5568'
  },
  deadline: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    color: '#4a5568'
  },
  progressSection: {
    marginBottom: '16px'
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#718096',
    marginBottom: '6px'
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px'
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  slider: {
    width: '100%',
    cursor: 'pointer'
  },
  deleteBtn: {
    padding: '6px 12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalTitle: {
    margin: '0 0 24px 0',
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a1a2e'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  inputGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  cancelBtn: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#f7f8fc',
    color: '#4a5568',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  submitBtn: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  }
};

export default Projects;